import {
  IGeneralStoreGame,
  IPlayerOption,
  ITeamPlayers,
  ITeams,
  ITwoRowGenericSelectorInp,
  TTeam,
} from '@/types';
import { useGameStateStore } from './gameState';
import { defineStore } from 'pinia';
import { find } from 'lodash';
import { getAllGames } from '@/api/requests';
import { TGameSnapshotService } from './util/gameSnapshotService';
import { THeartbeatService } from './util/heartbeatService';
import { usePlayFlowState } from './playFlow';
import { useUserStore } from './user';
import { ref, computed } from 'vue';
import { deleteUrlSearchParams, getUrlSearchParam, setUrlSearchParam } from '@/utilities';
import { PLAYER_ATTRIBUTION_INPUT } from '@/constants';
import moment from 'moment';
import { retrievePlayerData } from './util/generalStoreHelper';

export const useGeneralState = defineStore('general', () => {
  const season = getUrlSearchParam('season');
  const selectedYear = ref<string>(season || moment().format('YYYY'));
  const seasonYearsFetched = ref<string[]>([]);
  const games = ref<IGeneralStoreGame[]>([]);
  const currentGame = ref<IGeneralStoreGame | null>(null);
  const isLoading = ref<boolean>(false);
  const currentGamePlayers = ref<ITeamPlayers>({
    home: {
      Passer: [],
      Rusher: [],
      Receiver: [],
    },
    away: {
      Passer: [],
      Rusher: [],
      Receiver: [],
    },
    all: [],
  });

  const gameStateStore = useGameStateStore();
  const playflowStore = usePlayFlowState();
  const gameSnapshotService = new TGameSnapshotService(playflowStore, gameStateStore, useUserStore());
  const heartbeatService = new THeartbeatService();

  const defaultStore = () => {
    selectedYear.value = season || moment().format('YYYY');
    seasonYearsFetched.value = [];
    games.value = [];
    currentGame.value = null;
    isLoading.value = false;
    currentGamePlayers.value = {
      home: {
        Passer: [],
        Rusher: [],
        Receiver: [],
      },
      away: {
        Passer: [],
        Rusher: [],
        Receiver: [],
      },
      all: [],
    };
  };

  const fetchGames = async (year?: string, getPrevious: boolean = true) => {
    const selectedSeason = year || selectedYear.value;
    if (!seasonYearsFetched.value.includes(selectedSeason)) {
      isLoading.value = true;
      const seasonGames = await getAllGames(selectedSeason);
      seasonYearsFetched.value.push(selectedSeason);
      games.value = [
        ...games.value,
        ...seasonGames.map((game: IGeneralStoreGame) => ({ ...game, season: selectedSeason })),
      ];
    }
    if (getPrevious) {
      const previousSeason = (parseInt(selectedSeason, 10) - 1).toString();
      await fetchGames(previousSeason, false);
    }
    isLoading.value = false;
  };

  const updateCurrentYear = async (year: string) => {
    if (year !== selectedYear.value) {
      setUrlSearchParam('season', year);
      selectedYear.value = year;
      await fetchGames();
    }
  };

  const selectGame = async (gameId: number) => {
    currentGame.value = find(games.value, (game) => game.gameId === gameId);
    if (currentGame.value?.gameId) {
      gameSnapshotService.setGameId(currentGame.value.gameId);
      setUrlSearchParam('gameId', gameId);

      const alternateWeekMap: { [id: number]: number } = {
        8968: 18,
        8969: 18,
        8970: 18,
        8971: 18,
        8972: 19,
        8973: 19,
      };

      isLoading.value = true;
      currentGamePlayers.value = await retrievePlayerData(
        gameId,
        currentGame.value.season || selectedYear.value,
        alternateWeekMap[gameId] ?? currentGame.value.week,
        currentGame.value.awayTeamId,
        currentGame.value.homeTeamId
      );
      isLoading.value = false;
    }
  };

  const startContinuousUpdates = () => {
    if (currentGame.value.gameId && currentGame.value.gameId !== -1) {
      gameSnapshotService.startSnapshotUpdates();
    }
  };

  const setInitialState = async () => {
    isLoading.value = true;
    await gameSnapshotService.getGameSnapshot(true);
    isLoading.value = false;
  };

  const clearCurrentGame = () => {
    deleteUrlSearchParams();
    currentGame.value = null;
    gameSnapshotService.stopSnapshotUpdates();
    gameStateStore.resetGameDefaults();
    playflowStore.plays = [];
  };

  const startHeartbeat = () => {
    const { getUserRole } = useUserStore();
    const role = getUserRole();
    heartbeatService.startHeartbeatUpdates(currentGame.value.gameId, role);
  };

  const stopHeartbeat = () => {
    heartbeatService.stopHeartbeatUpdates();
  };

  const teams = computed((): ITeams | null => {
    if (!currentGame.value) {
      return null;
    }

    return {
      home: {
        id: currentGame.value.homeTeamId,
        abbreviation: currentGame.value.homeAbbr,
        name: currentGame.value.homeCollName,
        logoUrl: `https://nfl-draft-site-images-public.s3.us-east-2.amazonaws.com/team_logo_${currentGame.value.homeTeamId}.png`,
      },
      away: {
        id: currentGame.value.awayTeamId,
        abbreviation: currentGame.value.awayAbbr,
        name: currentGame.value.awayCollName,
        logoUrl: `https://nfl-draft-site-images-public.s3.us-east-2.amazonaws.com/team_logo_${currentGame.value.awayTeamId}.png`,
      },
    };
  });

  const currentGameQuarterbacks = computed((): { home: IPlayerOption; away: IPlayerOption } => ({
    home: currentGamePlayers.value.home.Passer[0],
    away: currentGamePlayers.value.away.Passer[0],
  }));

  const getPlayerAttributionOptions = (team: TTeam, backTo: string): ITwoRowGenericSelectorInp => {
    const rushers = currentGamePlayers.value[team].Rusher;
    const receivers = currentGamePlayers.value[team].Receiver;
    return PLAYER_ATTRIBUTION_INPUT(rushers, receivers, backTo);
  };

  const getPlayerInfoById = (id: number): IPlayerOption => {
    const playerOpts = currentGamePlayers.value.all;
    return find(playerOpts, (opt) => opt.playerId === id);
  };

  return {
    games,
    currentGame,
    isLoading,
    defaultStore,
    updateCurrentYear,
    fetchGames,
    selectGame,
    startContinuousUpdates,
    setInitialState,
    clearCurrentGame,
    getPlayerAttributionOptions,
    getPlayerInfoById,
    startHeartbeat,
    stopHeartbeat,
    currentGameQuarterbacks,
    teams,
    seasonYearsFetched,
    currentGamePlayers,
  };
});
